import type { ReactNode } from "react";
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarRange,
  Camera,
  CircleDashed,
  LayoutGrid,
  Megaphone,
  Sparkles,
} from "lucide-react";
import type { PortfolioPageProps, PortfolioProject } from "@/components/ui/starfall-portfolio-landing";

export interface PortfolioCaseStudy {
  id: string;
  client: string;
  title_es: string;
  title_en: string;
  description_es: string;
  description_en: string;
  category: string;
  thumbnail?: string;
  images?: string[];
  results_es?: string;
  results_en?: string;
  external_links?: Array<{
    label: string;
    href: string;
  }>;
}

type Locale = "en" | "es";

interface CategoryDefinition {
  key: string;
  icon: ReactNode;
  title: string;
  description: string;
}

interface PortfolioChromeContent extends Omit<PortfolioPageProps, "projects" | "loading" | "emptyMessage" | "activeCategory" | "onCategoryChange"> {}

const PROFESSIONAL_CHAMELEON_LINKS = [
  { label: "iHeart", href: "https://iheart.com/podcast/331039271/" },
  { label: "Spotify", href: "https://open.spotify.com/show/4jY5GZY8pexXwjJ5J5KPlt?si=hsrmhXtyRQao593yfM9fCg" },
  { label: "Apple Podcasts", href: "https://podcasts.apple.com/us/podcast/professional-chameleon/id1895020478" },
  { label: "YouTube", href: "https://youtube.com/professionalchameleon" },
  { label: "Amazon Music", href: "https://music.amazon.com/podcasts/bd906130-be59-45cf-aa20-60cbd33461ee/professional-chameleon" },
];

const PROFESSIONAL_CHAMELEON_ROUTE = "/podcast";

const FALLBACK_IMAGE_BY_CATEGORY: Record<string, string> = {
  digital_marketing:
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80",
  social_media:
    "https://images.unsplash.com/photo-1611162616475-46b635cb6868?auto=format&fit=crop&w=1400&q=80",
  branding:
    "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1400&q=80",
  project_management:
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1400&q=80",
  content_creation:
    "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=1400&q=80",
  event_production:
    "https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1400&q=80",
};

const staticFallbackProjects = {
  en: [
    {
      id: "fallback-brand-launch",
      client: "Hospitality Brand",
      title: "Brand relaunch with a sharper market voice",
      description:
        "Strategy, social storytelling, and launch assets aligned into a single campaign built to move from awareness to bookings.",
      category: "branding",
      results: "Identity refresh, campaign toolkit, and launch support for multi-channel rollout.",
      imageUrl: FALLBACK_IMAGE_BY_CATEGORY.branding,
      tags: ["Brand Strategy", "Launch Assets", "Campaign Rollout"],
    },
    {
      id: "fallback-content-engine",
      client: "Regional Service Firm",
      title: "Content engine for a growth-stage service brand",
      description:
        "We paired creative direction with an editorial system so their team could publish with more consistency and better sales alignment.",
      category: "content_creation",
      results: "Photo/video direction, reusable templates, and a steadier publishing cadence.",
      imageUrl: FALLBACK_IMAGE_BY_CATEGORY.content_creation,
      tags: ["Content System", "Creative Direction", "Templates"],
    },
    {
      id: "fallback-event-program",
      client: "Corporate Events Team",
      title: "Event production support with tighter execution",
      description:
        "From planning to day-of delivery, the program was restructured to keep vendors, timelines, and guest experience fully aligned.",
      category: "event_production",
      results: "Run-of-show planning, vendor coordination, and production oversight.",
      imageUrl: FALLBACK_IMAGE_BY_CATEGORY.event_production,
      tags: ["Production", "Operations", "Experience Design"],
    },
  ],
  es: [
    {
      id: "fallback-brand-launch",
      client: "Marca de Hospitalidad",
      title: "Relanzamiento de marca con una voz de mercado más clara",
      description:
        "Unimos estrategia, storytelling social y assets de lanzamiento en una campaña diseñada para convertir visibilidad en reservas.",
      category: "branding",
      results: "Refresh de identidad, toolkit de campaña y apoyo para rollout multicanal.",
      imageUrl: FALLBACK_IMAGE_BY_CATEGORY.branding,
      tags: ["Estrategia de Marca", "Launch Assets", "Campaña"],
    },
    {
      id: "fallback-content-engine",
      client: "Firma Regional de Servicios",
      title: "Motor de contenido para una marca en etapa de crecimiento",
      description:
        "Combinamos dirección creativa con un sistema editorial para que su equipo publicara con más consistencia y mejor conexión comercial.",
      category: "content_creation",
      results: "Dirección de foto/video, plantillas reutilizables y un ritmo de publicación más sólido.",
      imageUrl: FALLBACK_IMAGE_BY_CATEGORY.content_creation,
      tags: ["Sistema de Contenido", "Dirección Creativa", "Plantillas"],
    },
    {
      id: "fallback-event-program",
      client: "Equipo de Eventos Corporativos",
      title: "Soporte de producción de eventos con ejecución más precisa",
      description:
        "Desde la planificación hasta la entrega del día del evento, reestructuramos el programa para alinear suplidores, tiempos y experiencia.",
      category: "event_production",
      results: "Run of show, coordinación de suplidores y supervisión de producción.",
      imageUrl: FALLBACK_IMAGE_BY_CATEGORY.event_production,
      tags: ["Producción", "Operaciones", "Experiencia"],
    },
  ],
};

export const getPortfolioCategories = (locale: Locale): CategoryDefinition[] => {
  if (locale === "es") {
    return [
      {
        key: "all",
        icon: <LayoutGrid className="h-4 w-4" />,
        title: "Todo el trabajo",
        description: "Una vista completa del trabajo que desarrollamos entre estrategia, contenido y ejecución.",
      },
      {
        key: "digital_marketing",
        icon: <Megaphone className="h-4 w-4" />,
        title: "Marketing Digital",
        description: "Campañas y sistemas que convierten atención en oportunidades medibles.",
      },
      {
        key: "social_media",
        icon: <Sparkles className="h-4 w-4" />,
        title: "Redes Sociales",
        description: "Presencia social con dirección creativa, consistencia y narrativa de marca.",
      },
      {
        key: "branding",
        icon: <CircleDashed className="h-4 w-4" />,
        title: "Branding",
        description: "Identidades visuales y sistemas de marca listos para escalar.",
      },
      {
        key: "project_management",
        icon: <BriefcaseBusiness className="h-4 w-4" />,
        title: "Gestión de Proyectos",
        description: "Planificación, coordinación y accountability para proyectos complejos.",
      },
      {
        key: "content_creation",
        icon: <Camera className="h-4 w-4" />,
        title: "Contenido",
        description: "Foto, video y copy que hacen que la marca se vea y se sienta premium.",
      },
      {
        key: "event_production",
        icon: <CalendarRange className="h-4 w-4" />,
        title: "Eventos",
        description: "Producción y ejecución enfocadas en experiencia, detalle y control.",
      },
    ];
  }

  return [
    {
      key: "all",
      icon: <LayoutGrid className="h-4 w-4" />,
      title: "All work",
      description: "A full view of the work we build across strategy, content, and execution.",
    },
    {
      key: "digital_marketing",
      icon: <Megaphone className="h-4 w-4" />,
      title: "Digital Marketing",
      description: "Campaigns and systems designed to turn attention into measurable demand.",
    },
    {
      key: "social_media",
      icon: <Sparkles className="h-4 w-4" />,
      title: "Social Media",
      description: "Social presence with creative direction, consistency, and brand clarity.",
    },
    {
      key: "branding",
      icon: <CircleDashed className="h-4 w-4" />,
      title: "Branding",
      description: "Identity systems that make businesses feel more confident and more memorable.",
    },
    {
      key: "project_management",
      icon: <BriefcaseBusiness className="h-4 w-4" />,
      title: "Project Management",
      description: "Structure, coordination, and accountability for complex launches and teams.",
    },
    {
      key: "content_creation",
      icon: <Camera className="h-4 w-4" />,
      title: "Content Creation",
      description: "Photo, video, and copy designed to make the brand feel premium and current.",
    },
    {
      key: "event_production",
      icon: <CalendarRange className="h-4 w-4" />,
      title: "Event Production",
      description: "Production support that keeps guest experience and logistics in sync.",
    },
  ];
};

export const getPortfolioChromeContent = (locale: Locale): PortfolioChromeContent => {
  const isSpanish = locale === "es";
  const categories = getPortfolioCategories(locale);

  return {
    showAnimatedBackground: true,
    anchorLinks: isSpanish
      ? [
          { label: "Casos", href: "#portfolio-work" },
          { label: "Capacidades", href: "#portfolio-capabilities" },
          { label: "Proceso", href: "#portfolio-process" },
          { label: "Conectar", href: "#portfolio-cta" },
        ]
      : [
          { label: "Case Studies", href: "#portfolio-work" },
          { label: "Capabilities", href: "#portfolio-capabilities" },
          { label: "Process", href: "#portfolio-process" },
          { label: "Connect", href: "#portfolio-cta" },
        ],
    hero: isSpanish
      ? {
          eyebrow: "Portafolio Mikel Media",
          titleLine1: "Creamos marcas, campañas y experiencias",
          titleLine2Gradient: "listas para moverse con intención.",
          subtitle:
            "Este espacio reúne trabajo real en estrategia, contenido, gestión y producción. La idea no es solo que se vea bien, sino que funcione mejor para tu negocio.",
        }
      : {
          eyebrow: "Mikel Media Portfolio",
          titleLine1: "We build brands, campaigns, and experiences",
          titleLine2Gradient: "designed to move with intent.",
          subtitle:
            "This page brings together real work across strategy, content, project leadership, and production. The goal is not just to look good, but to work harder for the business behind it.",
        },
    ctaButtons: isSpanish
      ? {
          primary: { label: "Ver casos recientes", href: "#portfolio-work" },
          secondary: { label: "Coordinar una llamada", href: "/contacto" },
        }
      : {
          primary: { label: "Browse recent work", href: "#portfolio-work" },
          secondary: { label: "Book a strategy call", href: "/contacto" },
        },
    stats: isSpanish
      ? [
          { value: "150+", label: "proyectos entregados" },
          { value: "8+", label: "años liderando marcas" },
          { value: "PR + USA", label: "mercados activos" },
        ]
      : [
          { value: "150+", label: "projects delivered" },
          { value: "8+", label: "years leading brand work" },
          { value: "PR + USA", label: "active markets" },
        ],
    categories,
    categoriesHeading: isSpanish
      ? {
          eyebrow: "Capacidades",
          title: "Trabajo construido desde distintas disciplinas, con una misma lógica.",
          description:
            "Selecciona una categoría para filtrar los casos o recorre el conjunto completo. La estructura está pensada para crecer con más contenido sin rehacer la página.",
        }
      : {
          eyebrow: "Capabilities",
          title: "Work built across disciplines, guided by one consistent point of view.",
          description:
            "Filter by category or explore the full mix. The structure is designed to grow with more content over time without rebuilding the page.",
        },
    projectsHeading: isSpanish
      ? {
          eyebrow: "Casos seleccionados",
          title: "Historias recientes de trabajo con ejecución y criterio.",
          description:
            "Estas tarjetas combinan data editable del admin con una capa editorial más fuerte para que el portafolio cuente una historia, no solo una lista.",
        }
      : {
          eyebrow: "Selected work",
          title: "Recent projects shaped by strong execution and sharper positioning.",
          description:
            "These cards mix admin-managed case data with a more editorial front-end layer so the portfolio reads like a story, not just a list.",
        },
    loadingLabel: isSpanish ? "Cargando casos del portafolio" : "Loading portfolio stories",
    resultsLabel: isSpanish ? "Resultados / impacto" : "Results / impact",
    processHeading: isSpanish
      ? {
          eyebrow: "Cómo trabajamos",
          title: "Un proceso claro para transformar ideas en entregables que sostienen crecimiento.",
          description:
            "Cada proyecto cambia, pero la estructura de trabajo mantiene claridad, ritmo y accountability.",
        }
      : {
          eyebrow: "How we work",
          title: "A clear process for turning ideas into deliverables that support growth.",
          description:
            "Every engagement is different, but the underlying structure keeps clarity, pace, and accountability in place.",
        },
    process: isSpanish
      ? [
          {
            step: "01",
            title: "Diagnóstico y oportunidad",
            description:
              "Aterrizamos objetivos, restricciones y señales del mercado para definir qué realmente necesita cambiar.",
          },
          {
            step: "02",
            title: "Sistema creativo y operativo",
            description:
              "Traducimos la estrategia en dirección visual, narrativa, calendarios y prioridades accionables.",
          },
          {
            step: "03",
            title: "Producción y optimización",
            description:
              "Ejecutamos con seguimiento cercano para que el trabajo llegue bien resuelto y siga mejorando.",
          },
        ]
      : [
          {
            step: "01",
            title: "Diagnosis and opportunity",
            description:
              "We align on goals, constraints, and market signals to define what actually needs to change.",
          },
          {
            step: "02",
            title: "Creative and operating system",
            description:
              "Strategy becomes visual direction, messaging, timelines, and priorities the team can actually use.",
          },
          {
            step: "03",
            title: "Production and optimization",
            description:
              "We execute with close follow-through so the work lands polished and keeps improving after launch.",
          },
        ],
    testimonialHeading: isSpanish
      ? {
          eyebrow: "Prueba social",
          title: "Colaboraciones que fortalecen tanto el trabajo como el proceso.",
        }
      : {
          eyebrow: "Proof in the room",
          title: "Partnerships that strengthen both the work and the process.",
        },
    testimonial: isSpanish
      ? {
          quote:
            "Mikel Media nos ayudó a alinear estrategia, contenido y ejecución. El cambio no fue solo visual; hubo mucha más claridad interna y mejor respuesta del mercado.",
          author: "Equipo de marca regional",
          role: "Dirección de Marketing",
        }
      : {
          quote:
            "Mikel Media helped us align strategy, content, and execution. The shift was not only visual; it gave the team more internal clarity and a much stronger market response.",
          author: "Regional brand team",
          role: "Marketing leadership",
        },
    closingCta: isSpanish
      ? {
          eyebrow: "Siguiente paso",
          title: "Si el trabajo necesita más profundidad, esta página ya está lista para crecer.",
          description:
            "Podemos seguir sumando casos, resultados, testimonios o nuevas secciones sin desmontar la estructura base. Mientras tanto, ya tenemos una versión sólida para validar localmente y luego publicar.",
          primary: { label: "Hablemos del proyecto", href: "/contacto", icon: <ArrowRight className="h-4 w-4" /> },
          secondary: { label: "Ir al blog", href: "/blog" },
        }
      : {
          eyebrow: "Next step",
          title: "If the story needs more depth, this page is already set up to grow into it.",
          description:
            "We can keep layering in more case studies, results, testimonials, or editorial sections without reworking the foundation. For now, this gives us a strong version to validate locally before publishing.",
          primary: { label: "Start the conversation", href: "/contacto", icon: <ArrowRight className="h-4 w-4" /> },
          secondary: { label: "Read the blog", href: "/blog" },
        },
  };
};

export const mapCaseStudiesToProjects = (
  cases: PortfolioCaseStudy[],
  locale: Locale,
  getCategoryLabel: (category: string) => string,
): PortfolioProject[] => {
  const isProfessionalChameleon = (caseStudy: PortfolioCaseStudy) => {
    const searchableContent = [
      caseStudy.client,
      caseStudy.title_es,
      caseStudy.title_en,
      caseStudy.description_es,
      caseStudy.description_en,
    ]
      .join(" ")
      .toLowerCase();

    return searchableContent.includes("professional chameleon");
  };

  const getPlatformLinks = (caseStudy: PortfolioCaseStudy) => {
    if (caseStudy.external_links?.length) {
      return caseStudy.external_links.filter((link) => link?.label && link?.href);
    }

    if (isProfessionalChameleon(caseStudy)) {
      return PROFESSIONAL_CHAMELEON_LINKS;
    }

    return [];
  };

  return cases.map((caseStudy) => ({
    id: caseStudy.id,
    client: caseStudy.client,
    title: locale === "es" ? caseStudy.title_es : caseStudy.title_en,
    description: locale === "es" ? caseStudy.description_es : caseStudy.description_en,
    category: caseStudy.category,
    categoryLabel: getCategoryLabel(caseStudy.category),
    results: locale === "es" ? caseStudy.results_es : caseStudy.results_en,
    imageUrl:
      caseStudy.thumbnail ||
      caseStudy.images?.[0] ||
      FALLBACK_IMAGE_BY_CATEGORY[caseStudy.category] ||
      FALLBACK_IMAGE_BY_CATEGORY.digital_marketing,
    imageAlt:
      locale === "es"
        ? `Proyecto para ${caseStudy.client}`
        : `Project for ${caseStudy.client}`,
    tags: [getCategoryLabel(caseStudy.category), caseStudy.client].filter(Boolean),
    platformLinks: getPlatformLinks(caseStudy),
    primaryLink: isProfessionalChameleon(caseStudy)
      ? {
          label: locale === "es" ? "Ver pagina del podcast" : "Open podcast page",
          href: PROFESSIONAL_CHAMELEON_ROUTE,
        }
      : undefined,
  }));
};

export const getFallbackProjects = (
  locale: Locale,
  getCategoryLabel: (category: string) => string,
): PortfolioProject[] => {
  return staticFallbackProjects[locale].map((project) => ({
    ...project,
    categoryLabel: getCategoryLabel(project.category),
  }));
};
